/** Active speech occupies the composer input and reflects measured microphone volume. */
import React from 'react';
import {Pressable, StyleSheet, Text, View} from 'react-native';
import {useSharedPalette} from '../theme';

export interface QuantisAISpeechActivityProps {
  phase: 'listening' | 'waiting' | 'speaking' | 'ready';
  level: number;
  voice?: boolean;
  onStop: () => void;
}
export function QuantisAISpeechActivity({phase, level, voice = false, onStop}: QuantisAISpeechActivityProps) {
  const palette = useSharedPalette();
  const volume = Number.isFinite(level) ? Math.max(0, Math.min(1, level)) : 0;
  const listening = phase === 'listening';
  const label = listening ? voice ? 'Listening to your request' : 'Dictating' : phase === 'waiting' ? 'Waiting for QuantisAI' : phase === 'speaking' ? 'Reading the reply' : 'Voice ready';
  return <View testID="quantis-ai-speech-activity" style={styles.row}>
    <View style={styles.copy}>
      <Text accessibilityLiveRegion="polite" style={[styles.label, {color: palette.foreground}]}>{label}</Text>
      <Text style={[styles.hint, {color: palette.muted}]}>{listening ? voice ? 'Sends when you finish speaking' : 'Speech fills your draft' : phase === 'ready' ? 'Press Voice to speak again' : 'You can stop at any time'}</Text>
    </View>
    {listening && <View accessibilityRole="progressbar" accessibilityLabel="Microphone input level"
      accessibilityValue={{min: 0, max: 100, now: Math.round(volume * 100)}}
      aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(volume * 100)} style={styles.meter}>
      {Array.from({length: 28}, (_, index) => <View key={index} style={{width: 3, flexShrink: 1, borderRadius: 2,
        height: 3 + Math.sqrt(volume) * (10 + 22 * Math.sin(Math.PI * (index + 1) / 29)), backgroundColor: palette.brand}} />)}
    </View>}
    <Pressable accessibilityRole="button" accessibilityLabel={voice ? 'End voice' : 'Stop dictation'} onPress={onStop}
      style={({pressed}) => [styles.stop, {backgroundColor: palette.track, opacity: pressed ? 0.6 : 1}]}>
      <View style={{width: 10, height: 10, borderRadius: 2, backgroundColor: palette.foreground}} />
    </Pressable>
  </View>;
}
const styles = StyleSheet.create({
  row: {minHeight: 48, flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 8},
  copy: {flexShrink: 1, minWidth: 0},
  label: {fontSize: 13, lineHeight: 18, fontWeight: '500'},
  hint: {fontSize: 11, lineHeight: 16},
  meter: {flex: 1, minWidth: 30, maxWidth: 400, height: 36, flexDirection: 'row', gap: 3, alignItems: 'center', justifyContent: 'center', marginLeft: 'auto'},
  stop: {marginLeft: 'auto', width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center', flexShrink: 0},
});
